import React, { useState, useEffect } from 'react';
import { formatCurrency as canonicalFormatCurrency, formatMoneyOrNotComputed, NOT_ON_FILE_TEXT } from '../../utils/financialFormat';
import {
  BarChart3, 
  TrendingUp, 
  History,
  Play,
  RefreshCw,
  Info,
  DollarSign,
  Target,
  Zap,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Layers,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Lightbulb,
  Calculator,
  FileText,
  Users,
  Clock,
  Award,
  Scale,
  Wrench,
  Plus,
  ChevronDown,
  ChevronUp,
  Code
} from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';

interface TornadoDataItem {
  variable: string;
  low_value: number;
  high_value: number;
  low_outcome: number;
  high_outcome: number;
  swing: number;
  swing_pct: number;
  rank: number;
  base_value: number;
  direction: string;
}

interface TopDriver {
  rank: number;
  variable: string;
  swing: number;
  swing_pct: number;
  elasticity: number;
  direction: string;
  impact_assessment: string;
  recommendation: string;
}

interface Insight {
  type: string;
  priority: number;
  title: string;
  description: string;
  recommendation: string;
  icon: string;
}

interface InteractionEffect {
  variable_1: string;
  variable_2: string;
  combined_effect: number;
  individual_sum: number;
  interaction_strength: number;
  synergy_type: string;
}

interface SensitivityResult {
  success: boolean;
  analysis_id: string;
  base_outcome: number;
  tornado_data: TornadoDataItem[];
  top_drivers: TopDriver[];
  insights: Insight[];
  interaction_effects: InteractionEffect[];
  elasticities: Record<string, number>;
  visualization_data: {
    tornado: any[];
    spider: any[];
    max_swing: number;
  };
}

interface HistoricalResult {
  success: boolean;
  current_position: {
    recovery_rate: number | null;
    percentile_rank: number | null;
    position_label: string | null;
    position_description: string | null;
  };
  benchmarks: {
    industry: string;
    event_type: string;
    sample_size: number;
    adjusted: {
      avg_recovery_rate: number;
      median_recovery_rate: number;
      p25_recovery_rate: number;
      p75_recovery_rate: number;
      p90_recovery_rate: number;
    };
  };
  percentile_values: {
    p25: number;
    p50: number;
    p75: number;
    p90: number;
    avg: number;
  };
  gap_analysis: {
    vs_median: number | null;
    vs_p75: number | null;
    potential_upside: number;
  };
  recommendations: Array<{
    priority: number;
    category: string;
    title: string;
    recommendation: string;
    rationale: string;
  }>;
}

interface WhatIfTemplate {
  id: string;
  name: string;
  description: string;
  scenario_type: string;
  modification_count: number;
  total_effort_cost: number;
  total_effort_hours: number;
  total_impact_pct: number;
  avg_success_probability: number;
}

interface WhatIfScenario {
  scenario_id: string;
  name: string;
  description: string;
  scenario_type: string;
  modification_count: number;
  modifications: Array<{
    name: string;
    description: string;
    effort_level: string;
    effort_cost: number;
    effort_hours: number;
    settlement_impact_pct: number;
    success_probability: number;
    basis?: string;
    basis_note?: string;
    engine_eligible?: boolean;
  }>;
  projected_settlement: number;
  total_effort_cost: number;
  total_effort_hours: number;
  roi: number;
  net_benefit: number;
  risk_adjusted_benefit: number;
}

interface WhatIfResult {
  success: boolean;
  comparison_id: string;
  base_settlement: number;
  provenance?: {
    base_settlement_source?: string;
    impact_basis?: string;
    note?: string;
  };
  scenarios: WhatIfScenario[];
  comparison_summary: {
    best_roi: { scenario: string; roi: number };
    best_net_benefit: { scenario: string; benefit: number };
    recommended: { scenario: string; rationale: string };
  };
  recommended_breakdown: {
    scenario_name: string;
    gross_benefit: number;
    total_cost: number;
    net_benefit: number;
    roi_percentage: number;
    modification_breakdown: Array<{
      name: string;
      effort_cost: number;
      dollar_impact: number;
      risk_adjusted_impact: number;
      individual_roi: number;
    }>;
  };
}

interface Modification {
  id: string;
  template_id: string;
  template_name: string;
  name: string;
  description: string;
  effort_level: string;
  effort_cost: number;
  effort_hours: number;
  settlement_impact_pct: number;
  success_probability: number;
  category: string;
  basis?: string;
  basis_note?: string;
  engine_eligible?: boolean;
}

interface LitigationResult {
  success: boolean;
  settlement_path: {
    offer_amount: number;
    npv: number;
  };
  litigation_path: {
    expected_value_npv: number;
    total_litigation_cost: number;
    time_to_resolution_months: number;
    win_probability: number;
    scenario_breakdown: {
      win_no_appeal: { probability: number; value: number; npv: number };
      win_appeal_upheld: { probability: number; value: number; npv: number };
      win_appeal_reversed: { probability: number; value: number; npv: number };
      lose_trial: { probability: number; value: number; npv: number };
    };
  };
  comparison: {
    settlement_advantage: number;
    advantage_percentage: number;
    break_even_settlement: number;
    recommended_counter_offer: number;
  };
  recommendation: {
    action: string;
    rationale: string;
    confidence: string;
  };
  litigation_cost_breakdown: {
    attorney_fees: number;
    expert_witnesses: number;
    court_costs: number;
    discovery_costs: number;
    total: number;
  };
}

// Delegates to the canonical money helper (design-law: whole-dollar, paren
// negatives, em-dash verified-zero, NOT RUN for null). Kept as a local name so
// the 51 call sites are unchanged; the many gradient/token design items in this
// dashboard are handled by the separate design-token pass.
const formatCurrency = (value: number | null | undefined): string => canonicalFormatCurrency(value);

const getImpactColor = (assessment: string): React.CSSProperties => {
  switch (assessment) {
    case 'critical': return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' };
    case 'high': return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' };
    case 'moderate': return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' };
    default: return { color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-elevated)' };
  }
};

const getPositionColor = (label: string | null): React.CSSProperties => {
  switch (label) {
    case 'Excellent': return { color: 'var(--color-verified)', backgroundColor: 'transparent', borderColor: 'var(--color-border)' };
    case 'Above Average': return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' };
    case 'Average': return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' };
    case 'Below Average': return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' };
    case 'Poor': return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' };
    default: return { color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' };
  }
};

interface SettlementIntelligenceDashboardProps {
  initialTab?: 'sensitivity' | 'historical' | 'whatif' | 'builder' | 'litigation';
  caseId?: string;
  claimValue?: number;
  embedded?: boolean;
  businessType?: string;
  eventType?: string;
}

export default function SettlementIntelligenceDashboard({
  initialTab = 'sensitivity',
  caseId: propCaseId,
  claimValue: propClaimValue,
  embedded = false,
  businessType: propBusinessType,
  eventType: propEventType
}: SettlementIntelligenceDashboardProps) {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'sensitivity' | 'historical' | 'whatif' | 'builder' | 'litigation'>(initialTab);

  // Sync the active tab when the parent changes initialTab (settlement sub-nav /
  // legacy hash). Replaces a `key={settlementTab}` remount on the consumer, which
  // needlessly tore down + refetched the whole dashboard on every sub-tab switch.
  useEffect(() => { setActiveTab(initialTab); }, [initialTab]);

  // Case Integration state
  const [availableCases, setAvailableCases] = useState<any[]>([]);
  const [selectedCaseId, setSelectedCaseId] = useState<string>('');
  const [caseData, setCaseData] = useState<any>(null);
  const [loadingCase, setLoadingCase] = useState(false);

  // Sensitivity state
  const [sensitivityResult, setSensitivityResult] = useState<SensitivityResult | null>(null);
  const [claimValue, setClaimValue] = useState<string>('500000');
  const [perturbationPct, setPerturbationPct] = useState<string>('20');
  
  // Historical state
  const [historicalResult, setHistoricalResult] = useState<HistoricalResult | null>(null);
  const [historicalClaim, setHistoricalClaim] = useState<string>('500000');
  const [settlementValue, setSettlementValue] = useState<string>('');
  const [industry, setIndustry] = useState<string>('general');
  const [eventType, setEventType] = useState<string>('general');

  // What-If state
  const [whatifTemplates, setWhatifTemplates] = useState<WhatIfTemplate[]>([]);
  const [whatifResult, setWhatifResult] = useState<WhatIfResult | null>(null);
  const [whatifClaim, setWhatifClaim] = useState<string>('500000');
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);

  // Build Your Own Strategy state
  const [allModifications, setAllModifications] = useState<Modification[]>([]);
  const [modsByCategory, setModsByCategory] = useState<Record<string, Modification[]>>({});
  const [selectedMods, setSelectedMods] = useState<string[]>([]);
  const [builderClaim, setBuilderClaim] = useState<string>('500000');
  const [builderResult, setBuilderResult] = useState<any>(null);

  // Litigation Economics state
  const [litigationResult, setLitigationResult] = useState<LitigationResult | null>(null);
  const [currentOffer, setCurrentOffer] = useState<string>('300000');
  const [hasCarrierOffer, setHasCarrierOffer] = useState<boolean | null>(null);
  const [litigationClaim, setLitigationClaim] = useState<string>('500000');
  const [complexity, setComplexity] = useState<string>('medium');
  const [docStrength, setDocStrength] = useState<string>('medium');
  const [feeArrangement, setFeeArrangement] = useState<string>('contingency');

  // Calculation details visibility state
  const [showLitigationCalcs, setShowLitigationCalcs] = useState<boolean>(false);
  const [showBuilderCalcs, setShowBuilderCalcs] = useState<boolean>(false);
  const [showWhatifCalcs, setShowWhatifCalcs] = useState<boolean>(false);

  // Update tab when initialTab prop changes (for embedded mode)
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  // Load cases on mount (skip if embedded with caseId)
  useEffect(() => {
    if (embedded && propCaseId) {
      // In embedded mode with a case ID, just load that case
      loadCaseData(propCaseId);
      setSelectedCaseId(propCaseId);
      return;
    }
    
    const loadCases = async () => {
      try {
        const data = await api.get('/api/v1/settlement-intelligence/cases?limit=50');
        if (data.cases) {
          setAvailableCases(data.cases);
        }
      } catch (error) {
        console.error('Failed to load cases:', error);
      }
    };
    loadCases();
  }, [embedded, propCaseId]);

  // Initialize claim values from props
  useEffect(() => {
    if (propClaimValue && propClaimValue > 0) {
      const claimStr = propClaimValue.toString();
      setClaimValue(claimStr);
      setHistoricalClaim(claimStr);
      setWhatifClaim(claimStr);
      setBuilderClaim(claimStr);
      setLitigationClaim(claimStr);
    }
  }, [propClaimValue]);

  // Initialize industry/eventType from props (case context)
  useEffect(() => {
    if (propBusinessType) setIndustry(propBusinessType);
  }, [propBusinessType]);

  useEffect(() => {
    if (propEventType) setEventType(propEventType);
  }, [propEventType]);

  // Load case data when selected
  const loadCaseData = async (reportId: string) => {
    if (!reportId) {
      setCaseData(null);
      return;
    }
    
    setLoadingCase(true);
    try {
      const data = await api.get(`/api/v1/settlement-intelligence/case/${reportId}`);
      setCaseData(data);
      
      // Auto-populate all fields from case data.
      // The canonical net claim passed via propClaimValue is authoritative — a CPA-attestable
      // figure that must not be silently overridden by the fetched settlement P50. Only fall back
      // to the fetched claim_value when no valid prop was supplied (standalone/non-embedded use).
      if (!(propClaimValue && propClaimValue > 0)) {
        const claimVal = data.settlement_values?.claim_value?.toString() || '500000';
        setClaimValue(claimVal);
        setHistoricalClaim(claimVal);
        setWhatifClaim(claimVal);
        setBuilderClaim(claimVal);
        setLitigationClaim(claimVal);
      }
      
      // Set litigation-specific values
      if (data.litigation_inputs) {
        setCurrentOffer(data.litigation_inputs.current_offer?.toString() || '');
        setHasCarrierOffer(
          data.litigation_inputs.has_carrier_offer ??
          (data.litigation_inputs.current_offer != null),
        );
        setDocStrength(data.litigation_inputs.documentation_strength || 'medium');
        setComplexity(data.litigation_inputs.complexity || 'medium');
      }
      
      // Set historical comparison values
      if (data.historical_inputs) {
        setIndustry(data.historical_inputs.industry || 'general');
        setEventType(data.historical_inputs.event_type || 'general');
      }
      
      // No success toast when the panel is embedded in the case workspace:
      // the attorney opened this case on purpose, so "Loaded case: <name>"
      // is confirming something they can already see.
      if (!embedded) {
        toast.success(`Loaded case: ${data.case_context?.claimant_name || data.case_context?.client_name || data.case_context?.business_name || reportId}`);
      }
    } catch (error) {
      console.error('Failed to load case data:', error);
      // Embedded in the case workspace this surfaced a toast the attorney
      // cannot act on; the panel already renders its own empty state.
      if (!embedded) {
        toast.error('Failed to load case data');
      }
    } finally {
      setLoadingCase(false);
    }
  };

  // Load what-if templates and modifications on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [templatesData, modsData] = await Promise.all([
          api.get('/api/v1/settlement-intelligence/whatif/templates'),
          api.get('/api/v1/settlement-intelligence/whatif/modifications')
        ]);
        setWhatifTemplates(templatesData.templates || []);
        setAllModifications(modsData.modifications || []);
        setModsByCategory(modsData.by_category || {});
      } catch (error) {
        console.error('Failed to load data:', error);
      }
    };
    loadData();
  }, []);

  const runSensitivityAnalysis = async () => {
    const claim = parseFloat(claimValue);
    if (isNaN(claim) || claim <= 0) {
      toast.error('Please enter a valid claim value');
      return;
    }

    setLoading(true);
    try {
      const data = await api.post('/api/v1/settlement-intelligence/sensitivity/quick', {
        claim_value: claim,
        perturbation_pct: parseFloat(perturbationPct) || 20
      });
      setSensitivityResult(data);
      toast.success('Sensitivity analysis complete!');
    } catch (error) {
      console.error('Sensitivity analysis error:', error);
      toast.error('Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const runHistoricalComparison = async () => {
    const claim = parseFloat(historicalClaim);
    if (isNaN(claim) || claim <= 0) {
      toast.error('Please enter a valid claim value');
      return;
    }

    setLoading(true);
    try {
      const settlement = settlementValue ? parseFloat(settlementValue) : undefined;
      const data = await api.post('/api/v1/settlement-intelligence/historical/compare', {
        claim_value: claim,
        settlement_value: settlement,
        industry: industry,
        event_type: eventType,
        loss_period_months: 12
      });
      setHistoricalResult(data);
      toast.success('Historical comparison complete!');
    } catch (error) {
      console.error('Historical comparison error:', error);
      toast.error('Comparison failed');
    } finally {
      setLoading(false);
    }
  };

  const runWhatIfAnalysis = async () => {
    const claim = parseFloat(whatifClaim);
    if (isNaN(claim) || claim <= 0) {
      toast.error('Please enter a valid claim value');
      return;
    }

    if (selectedTemplates.length === 0) {
      toast.error('Please select at least one scenario');
      return;
    }

    setLoading(true);
    try {
      const data = await api.post('/api/v1/settlement-intelligence/whatif/analyze', {
        base_settlement: claim,
        template_ids: selectedTemplates
      });
      setWhatifResult(data);
      toast.success('What-If analysis complete!');
    } catch (error) {
      console.error('What-If analysis error:', error);
      toast.error('Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleTemplate = (templateId: string) => {
    setSelectedTemplates(prev => 
      prev.includes(templateId) 
        ? prev.filter(t => t !== templateId)
        : [...prev, templateId]
    );
  };

  const getScenarioTypeIcon = (type: string) => {
    switch (type) {
      case 'documentation': return FileText;
      case 'expert_witness': return Users;
      case 'time_extension': return Clock;
      case 'negotiation_strategy': return Target;
      case 'policy_interpretation': return Award;
      default: return Lightbulb;
    }
  };

  const getRoiColor = (roi: number): React.CSSProperties => {
    if (roi > 200) return { color: 'var(--color-verified)', backgroundColor: 'transparent' };
    if (roi > 100) return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' };
    if (roi > 50) return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' };
    if (roi > 0) return { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' };
    return { color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-elevated)' };
  };

  // Build Your Own Strategy function
  const runBuildStrategy = async () => {
    const claim = parseFloat(builderClaim);
    if (isNaN(claim) || claim <= 0) {
      toast.error('Please enter a valid claim value');
      return;
    }
    if (selectedMods.length === 0) {
      toast.error('Please select at least one modification');
      return;
    }

    setLoading(true);
    try {
      const data = await api.post('/api/v1/settlement-intelligence/whatif/build-strategy', {
        base_settlement: claim,
        modification_ids: selectedMods
      });
      setBuilderResult(data);
      toast.success('Custom strategy built!');
    } catch (error) {
      console.error('Build strategy error:', error);
      toast.error('Failed to build strategy');
    } finally {
      setLoading(false);
    }
  };

  const toggleMod = (modId: string) => {
    setSelectedMods(prev =>
      prev.includes(modId)
        ? prev.filter(m => m !== modId)
        : [...prev, modId]
    );
  };

  // Litigation Economics function
  const runLitigationAnalysis = async () => {
    const offer = parseFloat(currentOffer);
    const claim = parseFloat(litigationClaim);
    if (isNaN(offer) || offer <= 0 || isNaN(claim) || claim <= 0) {
      toast.error('Please enter valid offer and claim values');
      return;
    }

    setLoading(true);
    try {
      const data = await api.post('/api/v1/settlement-intelligence/litigation/analyze', {
        current_offer: offer,
        claim_value: claim,
        complexity: complexity,
        documentation_strength: docStrength,
        fee_arrangement: feeArrangement
      });
      setLitigationResult(data);
      toast.success('Litigation analysis complete!');
    } catch (error) {
      console.error('Litigation analysis error:', error);
      toast.error('Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const getRecommendationStyle = (action: string): React.CSSProperties => {
    switch (action) {
      case 'accept': return { backgroundColor: 'var(--color-verified)', color: '#fff' };
      case 'negotiate': return { backgroundColor: 'var(--color-brand)', color: '#fff' };
      case 'reject_litigate': return { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' };
      default: return { backgroundColor: 'var(--color-text-muted)', color: '#fff' };
    }
  };

  // Tornado chart renderer
  const renderTornadoChart = (data: TornadoDataItem[], baseOutcome: number, maxSwing: number) => {
    if (!data || data.length === 0) return null;
    
    return (
      <div className="space-y-2">
        {data.map((item, idx) => {
          const lowDeviation = item.low_outcome - baseOutcome;
          const highDeviation = item.high_outcome - baseOutcome;
          const maxDeviation = maxSwing / 2;
          
          // Calculate positions (centered at 50%)
          const lowPos = Math.max(0, 50 + (lowDeviation / maxDeviation) * 45);
          const highPos = Math.min(100, 50 + (highDeviation / maxDeviation) * 45);
          
          return (
            <div key={idx} className="flex items-center gap-3">
              <div className="w-32 text-right text-sm font-medium truncate" style={{ color: 'var(--color-text-secondary)' }}>
                {item.variable}
              </div>
              <div className="flex-1 h-8 rounded relative overflow-hidden" style={{ backgroundColor: 'var(--chart-track)' }}>
                {/* Center line */}
                <div className="absolute left-1/2 top-0 bottom-0 w-px" style={{ backgroundColor: 'var(--chart-grid)' }} />

                {/* Bar */}
                <div
                  className="absolute top-1 bottom-1 rounded"
                  style={{
                    left: `${Math.min(lowPos, highPos)}%`,
                    width: `${Math.abs(highPos - lowPos)}%`,
                    backgroundColor: item.direction === 'positive' ? 'var(--chart-1)' : 'var(--chart-2)'
                  }}
                />

                {/* Labels */}
                <div
                  className="absolute top-1/2 -translate-y-1/2 text-xs"
                  style={{ left: `${Math.min(lowPos, highPos) - 8}%`, color: 'var(--color-text-secondary)' }}
                >
                  {formatCurrency(item.low_outcome)}
                </div>
                <div
                  className="absolute top-1/2 -translate-y-1/2 text-xs"
                  style={{ left: `${Math.max(lowPos, highPos) + 2}%`, color: 'var(--color-text-secondary)' }}
                >
                  {formatCurrency(item.high_outcome)}
                </div>
              </div>
              <div className="w-24 text-right">
                <span className="text-sm font-medium" style={{
                  color: item.swing_pct > 20 ? 'var(--color-needs-human-text)' :
                  item.swing_pct > 10 ? 'var(--color-needs-human-text)' : 'var(--color-text-secondary)'
                }}>
                  {formatCurrency(item.swing)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={embedded ? "p-4" : "p-6 max-w-7xl mx-auto"}>
      {/* Header - Hide in embedded mode */}
      {!embedded && (
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(to bottom right, var(--color-brand), var(--color-brand-hover))' }}>
              <BarChart3 className="w-6 h-6" style={{ color: 'var(--color-on-brand)' }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Settlement Intelligence</h1>
              <p style={{ color: 'var(--color-text-muted)' }}>Advanced sensitivity analysis, benchmarking, and scenario planning</p>
            </div>
          </div>

          {/* Case Selector */}
          <div className="flex items-center gap-3">
            <div className="min-w-[280px]">
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-muted)' }}>Load from Case</label>
              <select
                value={selectedCaseId}
                onChange={(e) => {
                  setSelectedCaseId(e.target.value);
                  loadCaseData(e.target.value);
                }}
                disabled={loadingCase}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 text-sm"
                style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                data-testid="case-selector"
              >
                <option value="">-- Select a case to auto-fill --</option>
                {availableCases.map((c: any) => (
                  <option key={c.id || c._id} value={c.id || c._id}>
                    {c.display_name || c.claimant_name || c.client_name || NOT_ON_FILE_TEXT} - {formatMoneyOrNotComputed(c.net_supported_claim ?? c.result?.final_claim_amount)}
                  </option>
                ))}
              </select>
            </div>
            {loadingCase && <RefreshCw className="w-5 h-5 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />}
            {caseData && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>
                <CheckCircle className="w-4 h-4" />
                <span>Case loaded</span>
              </div>
            )}
          </div>
        </div>

        {/* Case Context Banner */}
        {caseData && (
          <div className="mt-4 p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                  Analyzing: {caseData.case_context?.claimant_name || caseData.case_context?.client_name || caseData.case_context?.business_name || NOT_ON_FILE_TEXT}
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--color-needs-human-text)' }}>
                  {caseData.case_context?.event_type || NOT_ON_FILE_TEXT} • {caseData.case_context?.industry || NOT_ON_FILE_TEXT} •
                  Claim: {formatMoneyOrNotComputed(caseData.settlement_values?.claim_value)}
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedCaseId('');
                  setCaseData(null);
                }}
                className="text-xs"
                style={{ color: 'var(--color-needs-human-text)' }}
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b overflow-x-auto" style={{ borderColor: 'var(--color-border)' }}>
        {[
          { id: 'sensitivity', label: 'Sensitivity', icon: TrendingUp },
          { id: 'historical', label: 'Historical', icon: History },
          { id: 'whatif', label: 'What-If', icon: Lightbulb },
          { id: 'builder', label: 'Strategy Builder', icon: Wrench },
          { id: 'litigation', label: 'Litigation Economics', icon: Scale }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className="flex items-center gap-2 px-3 py-3 font-medium transition-colors border-b-2 -mb-px whitespace-nowrap"
            style={
              activeTab === tab.id
                ? { color: 'var(--color-needs-human-text)', borderColor: 'var(--color-brand)' }
                : { color: 'var(--color-text-muted)', borderColor: 'transparent' }
            }
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Sensitivity Analysis Tab */}
      {activeTab === 'sensitivity' && (
        <div className="space-y-6">
          {/* Input Form */}
          <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <DollarSign className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
              Quick Sensitivity Analysis
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Claim Value *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                  <input
                    type="number"
                    value={claimValue}
                    onChange={(e) => setClaimValue(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 border rounded-lg focus:ring-2" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                    placeholder="500000"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Perturbation (±%)
                </label>
                <input
                  type="number"
                  value={perturbationPct}
                  onChange={(e) => setPerturbationPct(e.target.value)}
                  className="w-full px-4 py-2.5 border rounded-lg focus:ring-2" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                  placeholder="20"
                  min="5"
                  max="50"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={runSensitivityAnalysis}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg disabled:opacity-50 transition-all font-medium" style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                >
                  {loading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <Play className="w-5 h-5" />
                  )}
                  {loading ? 'Analyzing...' : 'Run Analysis'}
                </button>
              </div>
            </div>
            
            <p className="text-sm flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
              <Info className="w-4 h-4" />
              Uses typical industry ratios to estimate expense components
            </p>
          </div>

          {/* Results */}
          {sensitivityResult && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Base Settlement</p>
                      <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                        {formatCurrency(sensitivityResult.base_outcome)}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                      <Target className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
                    </div>
                  </div>
                </div>
                <div className="rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Variables Analyzed</p>
                      <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                        {sensitivityResult.tornado_data.length}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'transparent' }}>
                      <Layers className="w-6 h-6" style={{ color: 'var(--color-verified)' }} />
                    </div>
                  </div>
                </div>
                <div className="rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Max Swing</p>
                      <p className="text-2xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
                        {formatCurrency(sensitivityResult.visualization_data?.max_swing || 0)}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                      <TrendingUp className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
                    </div>
                  </div>
                </div>
                <div className="rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Insights</p>
                      <p className="text-2xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
                        {sensitivityResult.insights.length}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                      <Zap className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Tornado Chart */}
              <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                  <BarChart3 className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                  Tornado Chart - Variable Impact
                </h3>
                <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
                  Shows how ±{perturbationPct}% changes in each variable affect the settlement amount
                </p>
                {renderTornadoChart(
                  sensitivityResult.tornado_data,
                  sensitivityResult.base_outcome,
                  sensitivityResult.visualization_data?.max_swing || sensitivityResult.tornado_data[0]?.swing || 100000
                )}
              </div>

              {/* Top Drivers */}
              <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                  <Target className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                  Top Value Drivers
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {sensitivityResult.top_drivers.map((driver, idx) => (
                    <div key={idx} className="p-4 border rounded-lg transition-colors" style={{ borderColor: 'var(--color-border)' }}>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={
                          driver.rank === 1 ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' } :
                          driver.rank === 2 ? { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' } :
                          { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                        }>
                          <span className="font-bold text-sm">#{driver.rank}</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{driver.variable}</p>
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={getImpactColor(driver.impact_assessment)}>
                              {driver.impact_assessment}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm">
                            <span style={{ color: 'var(--color-text-muted)' }}>
                              Swing: <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{formatCurrency(driver.swing)}</span>
                            </span>
                            <span style={{ color: 'var(--color-text-muted)' }}>
                              Elasticity: <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{driver.elasticity.toFixed(2)}</span>
                            </span>
                          </div>
                          <p className="text-sm mt-2" style={{ color: 'var(--color-text-secondary)' }}>{driver.recommendation}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Insights */}
              {sensitivityResult.insights.length > 0 && (
                <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                    <Zap className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                    Key Insights
                  </h3>
                  <div className="space-y-3">
                    {sensitivityResult.insights.map((insight, idx) => (
                      <div key={idx} className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                        <p className="font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>{insight.title}</p>
                        <p className="text-sm mb-2" style={{ color: 'var(--color-text-secondary)' }}>{insight.description}</p>
                        <p className="text-sm flex items-center gap-1" style={{ color: 'var(--color-needs-human-text)' }}>
                          <ChevronRight className="w-4 h-4" />
                          {insight.recommendation}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Interaction Effects */}
              {sensitivityResult.interaction_effects.length > 0 && (
                <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                    <Layers className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                    Interaction Effects
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                          <th className="text-left py-2 px-3 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Variable 1</th>
                          <th className="text-left py-2 px-3 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Variable 2</th>
                          <th className="text-right py-2 px-3 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Combined Effect</th>
                          <th className="text-right py-2 px-3 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Individual Sum</th>
                          <th className="text-center py-2 px-3 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sensitivityResult.interaction_effects.slice(0, 5).map((effect, idx) => (
                          <tr key={idx} className="border-b" style={{ borderColor: 'var(--color-border-faint)' }}>
                            <td className="py-2 px-3 text-sm">{effect.variable_1}</td>
                            <td className="py-2 px-3 text-sm">{effect.variable_2}</td>
                            <td className="py-2 px-3 text-sm text-right font-medium">
                              {formatCurrency(effect.combined_effect)}
                            </td>
                            <td className="py-2 px-3 text-sm text-right">
                              {formatCurrency(effect.individual_sum)}
                            </td>
                            <td className="py-2 px-3 text-center">
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={
                                effect.synergy_type === 'amplifying' ? { backgroundColor: 'transparent', color: 'var(--color-verified)' } :
                                effect.synergy_type === 'dampening' ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' } :
                                { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }
                              }>
                                {effect.synergy_type}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Historical Comparison Tab */}
      {activeTab === 'historical' && (
        <div className="space-y-6">
          {/* Input Form */}
          <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <History className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              Compare Against Historical Settlements
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Claim Value *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                  <input
                    type="number"
                    value={historicalClaim}
                    onChange={(e) => setHistoricalClaim(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 border rounded-lg focus:ring-2" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                    placeholder="500000"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Settlement (optional)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                  <input
                    type="number"
                    value={settlementValue}
                    onChange={(e) => setSettlementValue(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 border rounded-lg focus:ring-2" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                    placeholder="375000"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Industry
                </label>
                <select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="w-full px-4 py-2.5 border rounded-lg focus:ring-2" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                >
                  <option value="general">General</option>
                  <option value="restaurant">Restaurant</option>
                  <option value="retail">Retail</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="hospitality">Hospitality</option>
                  <option value="healthcare">Healthcare</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Event Type
                </label>
                <select
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                  className="w-full px-4 py-2.5 border rounded-lg focus:ring-2" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                >
                  <option value="general">General</option>
                  <option value="fire">Fire</option>
                  <option value="flood">Flood</option>
                  <option value="hurricane">Hurricane</option>
                  <option value="earthquake">Earthquake</option>
                  <option value="cyberattack">Cyber Attack</option>
                  <option value="covid">COVID</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={runHistoricalComparison}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg disabled:opacity-50 transition-all font-medium" style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                >
                  {loading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <Play className="w-5 h-5" />
                  )}
                  Compare
                </button>
              </div>
            </div>
          </div>

          {/* Results */}
          {historicalResult && (
            <>
              {/* Position Card */}
              {historicalResult.current_position.percentile_rank !== null && (
                <div className="rounded-xl p-6 border-2" style={getPositionColor(historicalResult.current_position.position_label)}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-lg font-semibold">
                        Your Position: {historicalResult.current_position.position_label}
                      </p>
                      <p className="text-sm mt-1 opacity-80">
                        {historicalResult.current_position.position_description}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold">
                        {historicalResult.current_position.percentile_rank?.toFixed(0)}th
                      </p>
                      <p className="text-sm opacity-80">percentile</p>
                    </div>
                  </div>
                  <div className="mt-4 h-4 bg-white/50 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-current rounded-full transition-all"
                      style={{ width: `${historicalResult.current_position.percentile_rank}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1 text-xs opacity-70">
                    <span>0%</span>
                    <span>25%</span>
                    <span>50%</span>
                    <span>75%</span>
                    <span>100%</span>
                  </div>
                </div>
              )}

              {/* Benchmark Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Your Recovery Rate</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                    {historicalResult.current_position.recovery_rate?.toFixed(1) || '--'}%
                  </p>
                </div>
                <div className="rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Industry Median</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
                    {historicalResult.benchmarks.adjusted.median_recovery_rate.toFixed(1)}%
                  </p>
                </div>
                <div className="rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>75th Percentile</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-verified)' }}>
                    {historicalResult.benchmarks.adjusted.p75_recovery_rate.toFixed(1)}%
                  </p>
                </div>
                <div className="rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Sample Size</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-text-secondary)' }}>
                    {historicalResult.benchmarks.sample_size.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Percentile Values */}
              <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
                  Settlement Values by Percentile
                </h3>
                <div className="space-y-3">
                  {[
                    { label: '25th Percentile', key: 'p25', rate: historicalResult.benchmarks.adjusted.p25_recovery_rate },
                    { label: 'Median (50th)', key: 'p50', rate: historicalResult.benchmarks.adjusted.median_recovery_rate },
                    { label: '75th Percentile', key: 'p75', rate: historicalResult.benchmarks.adjusted.p75_recovery_rate },
                    { label: '90th Percentile', key: 'p90', rate: historicalResult.benchmarks.adjusted.p90_recovery_rate }
                  ].map((item) => {
                    const value = historicalResult.percentile_values[item.key as keyof typeof historicalResult.percentile_values];
                    const currentRate = historicalResult.current_position.recovery_rate;
                    const isCurrent = currentRate && Math.abs(currentRate - item.rate) < 3;
                    
                    return (
                      <div key={item.key} className="flex items-center gap-4 p-3 rounded-lg border" style={
                        isCurrent ? { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' } : { backgroundColor: 'var(--color-bg-elevated)', borderColor: 'transparent' }
                      }>
                        <div className="w-32 text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>{item.label}</div>
                        <div className="flex-1 h-3 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--chart-track)' }}>
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${item.rate}%`,
                              backgroundColor:
                                item.key === 'p90' ? 'var(--chart-positive)' :
                                item.key === 'p75' ? 'var(--chart-1)' :
                                item.key === 'p50' ? 'var(--chart-2)' :
                                'var(--chart-5)'
                            }}
                          />
                        </div>
                        <div className="w-28 text-right">
                          <span className="font-semibold">{formatCurrency(value)}</span>
                          <span className="text-xs ml-1" style={{ color: 'var(--color-text-muted)' }}>({item.rate.toFixed(0)}%)</span>
                        </div>
                        {isCurrent && (
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>YOU</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Gap Analysis */}
              {historicalResult.current_position.recovery_rate !== null && (
                <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Gap Analysis</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>vs. Median</p>
                      <div className="flex items-center gap-2 mt-1">
                        {historicalResult.gap_analysis.vs_median && historicalResult.gap_analysis.vs_median > 0 ? (
                          <ArrowUpRight className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                        ) : historicalResult.gap_analysis.vs_median && historicalResult.gap_analysis.vs_median < 0 ? (
                          <ArrowDownRight className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                        ) : (
                          <Minus className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                        )}
                        <span className="text-xl font-bold" style={{
                          color:
                            (historicalResult.gap_analysis.vs_median || 0) > 0 ? 'var(--color-verified)' :
                            (historicalResult.gap_analysis.vs_median || 0) < 0 ? 'var(--color-needs-human-text)' :
                            'var(--color-text-secondary)'
                        }}>
                          {formatMoneyOrNotComputed(historicalResult.gap_analysis.vs_median)}
                        </span>
                      </div>
                    </div>
                    <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Gap to 75th %</p>
                      <p className="text-xl font-bold mt-1" style={{ color: 'var(--color-needs-human-text)' }}>
                        {formatMoneyOrNotComputed(historicalResult.gap_analysis.vs_p75)}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg" style={{ backgroundColor: 'transparent' }}>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Potential Upside</p>
                      <p className="text-xl font-bold mt-1" style={{ color: 'var(--color-verified)' }}>
                        {formatCurrency(historicalResult.gap_analysis.potential_upside)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {historicalResult.recommendations.length > 0 && (
                <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                    <Zap className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                    Recommendations
                  </h3>
                  <div className="space-y-3">
                    {historicalResult.recommendations.map((rec, idx) => (
                      <div key={idx} className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                            <span className="font-bold text-sm" style={{ color: 'var(--color-needs-human-text)' }}>{rec.priority}</span>
                          </div>
                          <div>
                            <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{rec.title}</p>
                            <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>{rec.recommendation}</p>
                            <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>{rec.rationale}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* What-If Modeler Tab */}
      {activeTab === 'whatif' && (
        <div className="space-y-6">
          {/* Input Form */}
          <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <Lightbulb className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              Scenario Planning
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
              Compare different approaches to maximize your settlement. Select scenarios to analyze their ROI.
            </p>
            
            <div className="flex gap-4 mb-6">
              <div className="flex-1 max-w-xs">
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Base Settlement *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                  <input
                    type="number"
                    value={whatifClaim}
                    onChange={(e) => setWhatifClaim(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 border rounded-lg focus:ring-2" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                    placeholder="500000"
                    data-testid="whatif-base-settlement"
                  />
                </div>
              </div>
              <div className="flex items-end">
                <button
                  onClick={runWhatIfAnalysis}
                  disabled={loading || selectedTemplates.length === 0}
                  className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg disabled:opacity-50 transition-all font-medium" style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                  data-testid="whatif-analyze-btn"
                >
                  {loading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <Calculator className="w-5 h-5" />
                  )}
                  {loading ? 'Analyzing...' : 'Compare Scenarios'}
                </button>
              </div>
            </div>

            {/* Scenario Templates */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {whatifTemplates.map((template) => {
                const IconComponent = getScenarioTypeIcon(template.scenario_type);
                const isSelected = selectedTemplates.includes(template.id);
                
                return (
                  <div
                    key={template.id}
                    onClick={() => toggleTemplate(template.id)}
                    className="p-4 rounded-xl border-2 cursor-pointer transition-all"
                    style={
                      isSelected
                        ? { borderColor: 'var(--color-brand)', backgroundColor: 'var(--color-needs-human-tint)' }
                        : { borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }
                    }
                    data-testid={`whatif-template-${template.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={
                        isSelected ? { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' } : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }
                      }>
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>{template.name}</p>
                        <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--color-text-muted)' }}>{template.description}</p>
                      </div>
                      {isSelected && (
                        <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-needs-human-text)' }} />
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      <span className="px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>
                        {formatCurrency(template.total_effort_cost)} cost
                      </span>
                      <span className="px-2 py-0.5 rounded" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>
                        +{template.total_impact_pct.toFixed(0)}% impact
                      </span>
                      <span className="px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                        {(template.avg_success_probability * 100).toFixed(0)}% success
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {selectedTemplates.length > 0 && (
              <p className="mt-4 text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
                {selectedTemplates.length} scenario{selectedTemplates.length > 1 ? 's' : ''} selected
              </p>
            )}
          </div>

          {/* What-If Results */}
          {whatifResult && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Base Settlement</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                    {formatCurrency(whatifResult.base_settlement)}
                  </p>
                  {whatifResult.provenance?.base_settlement_source === 'case_engine' && (
                    <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                      Engine-derived
                    </p>
                  )}
                </div>
                <div className="rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Scenarios Analyzed</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
                    {whatifResult.scenarios.length}
                  </p>
                </div>
                <div className="rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Best ROI</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-verified)' }}>
                    {whatifResult.comparison_summary.best_roi.roi.toFixed(0)}%
                  </p>
                </div>
                <div className="rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Best Net Benefit</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
                    {formatCurrency(whatifResult.comparison_summary.best_net_benefit.benefit)}
                  </p>
                </div>
              </div>

              {/* Provenance banner — honest about what is engine-computed vs a leverage estimate */}
              {whatifResult.provenance && (
                <div
                  className="rounded-xl p-4 border flex items-start gap-3"
                  style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}
                  data-testid="whatif-provenance-banner"
                >
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />
                  <div className="text-sm">
                    <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>How to read these numbers</p>
                    <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                      {whatifResult.provenance.note
                        || 'Base settlement is the case’s engine-derived figure. Impact percentages are leverage estimates — analyst assumptions, not computed from this case’s financials.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Recommendation Banner */}
              <div className="rounded-xl p-6" style={{ background: 'linear-gradient(to right, var(--color-brand), var(--color-brand-hover))', color: 'var(--color-on-brand)' }}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--color-on-brand)', opacity: 0.85 }}>Recommended Approach</p>
                    <p className="text-xl font-bold mt-1">
                      {whatifResult.comparison_summary.recommended.scenario}
                    </p>
                    <p className="mt-2 text-sm" style={{ color: 'var(--color-on-brand)', opacity: 0.85 }}>
                      {whatifResult.comparison_summary.recommended.rationale}
                    </p>
                  </div>
                </div>
              </div>

              {/* Scenario Comparison */}
              <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                  <BarChart3 className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                  Scenario Comparison
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                        <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Scenario</th>
                        <th className="text-right py-3 px-4 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Cost</th>
                        <th className="text-right py-3 px-4 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Projected</th>
                        <th className="text-right py-3 px-4 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Net Benefit</th>
                        <th className="text-right py-3 px-4 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>ROI</th>
                        <th className="text-right py-3 px-4 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Risk-Adjusted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {whatifResult.scenarios.map((scenario, idx) => {
                        const isRecommended = scenario.name === whatifResult.comparison_summary.recommended.scenario;
                        return (
                          <tr
                            key={idx}
                            className="border-b"
                            style={{ borderColor: 'var(--color-border-faint)', backgroundColor: isRecommended ? 'var(--color-needs-human-tint)' : 'transparent' }}
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                {isRecommended && <Award className="w-4 h-4" style={{ color: 'var(--color-needs-human-text)' }} />}
                                <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{scenario.name}</span>
                              </div>
                              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{scenario.modification_count} modifications</p>
                            </td>
                            <td className="py-3 px-4 text-right text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                              {formatCurrency(scenario.total_effort_cost)}
                            </td>
                            <td className="py-3 px-4 text-right text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                              {formatCurrency(scenario.projected_settlement)}
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="font-medium" style={{ color: scenario.net_benefit > 0 ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}>
                                {formatCurrency(scenario.net_benefit)}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="px-2 py-0.5 rounded-full text-sm font-medium" style={getRoiColor(scenario.roi)}>
                                {scenario.roi.toFixed(0)}%
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                              {formatCurrency(scenario.risk_adjusted_benefit)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ROI Breakdown for Recommended */}
              {whatifResult.recommended_breakdown && (
                <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                    <Calculator className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                    ROI Breakdown: {whatifResult.recommended_breakdown.scenario_name}
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Gross Benefit</p>
                      <p className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                        {formatCurrency(whatifResult.recommended_breakdown.gross_benefit)}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Total Cost</p>
                      <p className="text-xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
                        -{formatCurrency(whatifResult.recommended_breakdown.total_cost)}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg" style={{ backgroundColor: 'transparent' }}>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Net Benefit</p>
                      <p className="text-xl font-bold" style={{ color: 'var(--color-verified)' }}>
                        {formatCurrency(whatifResult.recommended_breakdown.net_benefit)}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>ROI</p>
                      <p className="text-xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
                        {whatifResult.recommended_breakdown.roi_percentage.toFixed(0)}%
                      </p>
                    </div>
                  </div>

                  {/* Modification Breakdown */}
                  <h4 className="font-medium mb-3" style={{ color: 'var(--color-text-primary)' }}>Modification Impact</h4>
                  <div className="space-y-2">
                    {whatifResult.recommended_breakdown.modification_breakdown.map((mod, idx) => (
                      <div key={idx} className="flex items-center gap-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                        <div className="flex-1">
                          <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{mod.name}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                            Cost: <span className="font-medium">{formatCurrency(mod.effort_cost)}</span>
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                            Impact: <span className="font-medium" style={{ color: 'var(--color-verified)' }}>{formatCurrency(mod.dollar_impact)}</span>
                          </p>
                        </div>
                        <div className="w-20 text-right">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={getRoiColor(mod.individual_roi)}>
                            {mod.individual_roi.toFixed(0)}% ROI
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Strategy Builder Tab */}
      {activeTab === 'builder' && (
        <div className="space-y-6">
          <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <Wrench className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              Build Your Own Strategy
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
              Mix and match individual modifications from any scenario template to create your optimal strategy.
            </p>

            <div className="flex gap-4 mb-6">
              <div className="flex-1 max-w-xs">
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Base Settlement *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                  <input
                    type="number"
                    value={builderClaim}
                    onChange={(e) => setBuilderClaim(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 border rounded-lg focus:ring-2" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                    data-testid="builder-base-settlement"
                  />
                </div>
              </div>
              <div className="flex items-end">
                <button
                  onClick={runBuildStrategy}
                  disabled={loading || selectedMods.length === 0}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-lg disabled:opacity-50" style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                  data-testid="builder-analyze-btn"
                >
                  {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                  Build Strategy ({selectedMods.length})
                </button>
              </div>
            </div>

            {/* Modifications by Category */}
            {Object.entries(modsByCategory).map(([category, mods]) => (
              <div key={category} className="mb-6">
                <h4 className="text-sm font-semibold mb-3 capitalize flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
                  {getScenarioTypeIcon(category) && React.createElement(getScenarioTypeIcon(category), { className: 'w-4 h-4' })}
                  {category.replace(/_/g, ' ')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {mods.map((mod: Modification) => {
                    const isSelected = selectedMods.includes(mod.id);
                    return (
                      <div
                        key={mod.id}
                        onClick={() => toggleMod(mod.id)}
                        className="p-3 rounded-lg border cursor-pointer transition-all"
                        style={
                          isSelected ? { borderColor: 'var(--color-brand)', backgroundColor: 'var(--color-needs-human-tint)' } : { borderColor: 'var(--color-border)' }
                        }
                      >
                        <div className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5" style={
                            isSelected ? { borderColor: 'var(--color-brand)', backgroundColor: 'var(--color-brand)' } : { borderColor: 'var(--color-border)' }
                          }>
                            {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{mod.name}</p>
                            <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{mod.description}</p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>{formatCurrency(mod.effort_cost)}</span>
                              <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>+{mod.settlement_impact_pct}%</span>
                              <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>{(mod.success_probability * 100).toFixed(0)}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Builder Results */}
          {builderResult && builderResult.success && (
            <>
            <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Custom Strategy Results</h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Base Settlement</p>
                  <p className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{formatCurrency(builderResult.scenario.base_settlement)}</p>
                </div>
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'transparent' }}>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Projected Settlement</p>
                  <p className="text-xl font-bold" style={{ color: 'var(--color-verified)' }}>{formatCurrency(builderResult.scenario.projected_settlement)}</p>
                </div>
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Total Cost</p>
                  <p className="text-xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>{formatCurrency(builderResult.scenario.total_effort_cost)}</p>
                </div>
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>ROI</p>
                  <p className="text-xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>{builderResult.scenario.roi.toFixed(0)}%</p>
                </div>
              </div>

              <div className="p-4 rounded-lg" style={{ background: 'linear-gradient(to right, var(--color-brand), var(--color-brand-hover))', color: 'var(--color-on-brand)' }}>
                <p className="text-sm opacity-80">Net Benefit</p>
                <p className="text-2xl font-bold">{formatCurrency(builderResult.scenario.net_benefit)}</p>
                <p className="text-sm opacity-80 mt-1">
                  Risk-adjusted: {formatCurrency(builderResult.scenario.risk_adjusted_benefit)}
                </p>
              </div>
            </div>

            {/* Strategy Builder Calculation Details */}
            <div className="rounded-xl shadow-sm border overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
              <button
                onClick={() => setShowBuilderCalcs(!showBuilderCalcs)}
                className="w-full p-4 flex items-center justify-between transition-colors"
                data-testid="show-builder-calcs"
              >
                <div className="flex items-center gap-2">
                  <Code className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                  <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Show Calculation Details</span>
                  <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>No AI - Pure Math</span>
                </div>
                {showBuilderCalcs ? <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} /> : <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />}
              </button>
              
              {showBuilderCalcs && (
                <div className="p-6 border-t" style={{ borderColor: 'var(--color-border-sub)', backgroundColor: 'var(--color-bg-elevated)' }}>
                  <div className="space-y-6 font-mono text-sm">
                    {/* Impact Calculation */}
                    <div>
                      <h5 className="font-semibold mb-2 font-sans" style={{ color: 'var(--color-text-primary)' }}>1. Settlement Impact (with Diminishing Returns)</h5>
                      <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                        <p className="mb-2" style={{ color: 'var(--color-text-secondary)' }}>effective_impact = raw_impact × 0.9^(modification_index)</p>
                        <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Each stacked modification has 10% less impact than previous</p>
                        <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
                          {builderResult.scenario.modifications?.map((mod: any, idx: number) => (
                            <p key={idx} style={{ color: 'var(--color-text-secondary)' }}>
                              <span style={{ color: 'var(--color-text-muted)' }}>Mod {idx + 1}:</span> {mod.settlement_impact_pct}% × 0.9^{idx} = {(mod.settlement_impact_pct * Math.pow(0.9, idx)).toFixed(2)}%
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Projected Settlement */}
                    <div>
                      <h5 className="font-semibold mb-2 font-sans" style={{ color: 'var(--color-text-primary)' }}>2. Projected Settlement</h5>
                      <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                        <p className="mb-2" style={{ color: 'var(--color-text-secondary)' }}>projected = base × (1 + total_impact%)</p>
                        <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
                          <p style={{ color: 'var(--color-text-secondary)' }}>
                            {formatCurrency(builderResult.scenario.base_settlement)} × (1 + {((builderResult.scenario.projected_settlement / builderResult.scenario.base_settlement - 1) * 100).toFixed(2)}%)
                          </p>
                          <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                            <span style={{ color: 'var(--color-text-muted)' }}>= </span>
                            <span className="font-bold">{formatCurrency(builderResult.scenario.projected_settlement)}</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* ROI Calculation */}
                    <div>
                      <h5 className="font-semibold mb-2 font-sans" style={{ color: 'var(--color-text-primary)' }}>3. ROI Calculation</h5>
                      <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                        <p className="mb-2" style={{ color: 'var(--color-text-secondary)' }}>ROI = ((projected - base - cost) / cost) × 100</p>
                        <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
                          <p style={{ color: 'var(--color-text-secondary)' }}>
                            Gross Benefit = {formatCurrency(builderResult.scenario.projected_settlement)} - {formatCurrency(builderResult.scenario.base_settlement)} = {formatCurrency(builderResult.scenario.projected_settlement - builderResult.scenario.base_settlement)}
                          </p>
                          <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                            Net Benefit = {formatCurrency(builderResult.scenario.projected_settlement - builderResult.scenario.base_settlement)} - {formatCurrency(builderResult.scenario.total_effort_cost)} = {formatCurrency(builderResult.scenario.net_benefit)}
                          </p>
                          <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                            ROI = ({formatCurrency(builderResult.scenario.net_benefit)} / {formatCurrency(builderResult.scenario.total_effort_cost)}) × 100 = <span className="font-bold">{builderResult.scenario.roi.toFixed(1)}%</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Risk-Adjusted Benefit */}
                    <div>
                      <h5 className="font-semibold mb-2 font-sans" style={{ color: 'var(--color-text-primary)' }}>4. Risk-Adjusted Benefit</h5>
                      <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                        <p className="mb-2" style={{ color: 'var(--color-text-secondary)' }}>risk_adjusted = net_benefit × combined_success_probability</p>
                        <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
                          <p style={{ color: 'var(--color-text-secondary)' }}>
                            Combined Success = {builderResult.scenario.modifications?.map((m: any) => `${(m.success_probability * 100).toFixed(0)}%`).join(' × ')}
                          </p>
                          <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                            Risk-Adjusted = {formatCurrency(builderResult.scenario.net_benefit)} × success_rate = <span className="font-bold">{formatCurrency(builderResult.scenario.risk_adjusted_benefit)}</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Disclaimer */}
                    <div className="p-3 rounded-lg border" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                      <p className="text-xs font-sans" style={{ color: 'var(--color-needs-human-text)' }}>
                        <strong>Note:</strong> These calculations use deterministic mathematical formulas with no AI/LLM involvement. 
                        Diminishing returns (0.9^n) prevent unrealistic stacking. Success probabilities are based on industry data for similar efforts.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            </>
          )}
        </div>
      )}

      {/* Litigation Economics Tab */}
      {activeTab === 'litigation' && (
        <div className="space-y-6">
          <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <Scale className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              Settlement vs Litigation Analysis
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
              Compare accepting a settlement offer against litigating. Uses NPV to account for time value and risk.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Current Offer *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                  <input
                    type="number"
                    value={currentOffer}
                    onChange={(e) => setCurrentOffer(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 border rounded-lg focus:ring-2" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                    data-testid="litigation-offer"
                  />
                </div>
                {hasCarrierOffer === false && (
                  <p className="text-xs mt-1" style={{ color: 'var(--color-needs-human-text)' }} data-testid="litigation-no-offer-hint">
                    No carrier offer on record — enter the carrier&rsquo;s actual offer. This tool does not assume one.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Full Claim Value *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                  <input
                    type="number"
                    value={litigationClaim}
                    onChange={(e) => setLitigationClaim(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 border rounded-lg focus:ring-2" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                    data-testid="litigation-claim"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Case Complexity</label>
                <select
                  value={complexity}
                  onChange={(e) => setComplexity(e.target.value)}
                  className="w-full px-4 py-2.5 border rounded-lg focus:ring-2" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="extreme">Extreme</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Documentation Strength</label>
                <select
                  value={docStrength}
                  onChange={(e) => setDocStrength(e.target.value)}
                  className="w-full px-4 py-2.5 border rounded-lg focus:ring-2" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                >
                  <option value="weak">Weak</option>
                  <option value="medium">Medium</option>
                  <option value="strong">Strong</option>
                  <option value="very_strong">Very Strong</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Fee Arrangement</label>
                <select
                  value={feeArrangement}
                  onChange={(e) => setFeeArrangement(e.target.value)}
                  className="w-full px-4 py-2.5 border rounded-lg focus:ring-2" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                >
                  <option value="contingency">Contingency (33-40%)</option>
                  <option value="hourly">Hourly</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={runLitigationAnalysis}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg disabled:opacity-50" style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                  data-testid="litigation-analyze-btn"
                >
                  {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Scale className="w-5 h-5" />}
                  Analyze
                </button>
              </div>
            </div>
          </div>

          {/* Litigation Results */}
          {litigationResult && (
            <>
              {/* Recommendation Banner */}
              <div className="rounded-xl p-6" style={getRecommendationStyle(litigationResult.recommendation.action)}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    {litigationResult.recommendation.action === 'accept' && <CheckCircle className="w-6 h-6" />}
                    {litigationResult.recommendation.action === 'negotiate' && <Target className="w-6 h-6" />}
                    {litigationResult.recommendation.action === 'reject_litigate' && <Scale className="w-6 h-6" />}
                  </div>
                  <div>
                    <p className="text-sm opacity-80 font-medium">Recommendation</p>
                    <p className="text-2xl font-bold capitalize">{litigationResult.recommendation.action.replace('_', ' ')}</p>
                    <p className="text-sm opacity-90 mt-2">{litigationResult.recommendation.rationale}</p>
                    <p className="text-xs opacity-70 mt-1">Confidence: {litigationResult.recommendation.confidence}</p>
                  </div>
                </div>
              </div>

              {/* Path Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Settlement Path */}
                <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-verified)' }}>
                  <h4 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                    <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                    Settlement Path
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--color-text-secondary)' }}>Offer Amount</span>
                      <span className="font-semibold">{formatCurrency(litigationResult.settlement_path.offer_amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--color-text-secondary)' }}>Net Present Value</span>
                      <span className="font-semibold" style={{ color: 'var(--color-verified)' }}>{formatCurrency(litigationResult.settlement_path.npv)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--color-text-secondary)' }}>Time to Payment</span>
                      <span className="font-semibold">Immediate</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--color-text-secondary)' }}>Certainty</span>
                      <span className="font-semibold" style={{ color: 'var(--color-verified)' }}>100%</span>
                    </div>
                  </div>
                </div>

                {/* Litigation Path */}
                <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                  <h4 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                    <Scale className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                    Litigation Path
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--color-text-secondary)' }}>Expected Value (NPV)</span>
                      <span className="font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>{formatCurrency(litigationResult.litigation_path.expected_value_npv)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--color-text-secondary)' }}>Win Probability</span>
                      <span className="font-semibold">{(litigationResult.litigation_path.win_probability * 100).toFixed(0)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--color-text-secondary)' }}>Total Litigation Cost</span>
                      <span className="font-semibold" style={{ color: 'var(--color-text-muted)' }}>-{formatCurrency(litigationResult.litigation_path.total_litigation_cost)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--color-text-secondary)' }}>Time to Resolution</span>
                      <span className="font-semibold">{litigationResult.litigation_path.time_to_resolution_months} months</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Key Metrics */}
              <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                <h4 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Key Metrics</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 rounded-lg text-center" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Settlement Advantage</p>
                    <p className="text-xl font-bold" style={{ color: litigationResult.comparison.settlement_advantage > 0 ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}>
                      {formatCurrency(litigationResult.comparison.settlement_advantage)}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg text-center" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Advantage %</p>
                    <p className="text-xl font-bold" style={{ color: litigationResult.comparison.advantage_percentage > 0 ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}>
                      {litigationResult.comparison.advantage_percentage.toFixed(0)}%
                    </p>
                  </div>
                  <div className="p-4 rounded-lg text-center" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Break-Even Settlement</p>
                    <p className="text-xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>{formatCurrency(litigationResult.comparison.break_even_settlement)}</p>
                  </div>
                  <div className="p-4 rounded-lg text-center" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Recommended Counter</p>
                    <p className="text-xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>{formatCurrency(litigationResult.comparison.recommended_counter_offer)}</p>
                  </div>
                </div>
              </div>

              {/* Cost Breakdown */}
              <div className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                <h4 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Litigation Cost Breakdown</h4>
                <div className="space-y-2">
                  {Object.entries(litigationResult.litigation_cost_breakdown).filter(([k]) => k !== 'total').map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center p-2 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                      <span className="text-sm capitalize" style={{ color: 'var(--color-text-secondary)' }}>{key.replace(/_/g, ' ')}</span>
                      <span className="font-medium">{formatCurrency(value as number)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center p-3 rounded-lg mt-2" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                    <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Total Estimated Cost</span>
                    <span className="font-bold" style={{ color: 'var(--color-needs-human-text)' }}>{formatCurrency(litigationResult.litigation_cost_breakdown.total)}</span>
                  </div>
                </div>
              </div>

              {/* Calculation Details - Collapsible */}
              <div className="rounded-xl shadow-sm border overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                <button
                  onClick={() => setShowLitigationCalcs(!showLitigationCalcs)}
                  className="w-full p-4 flex items-center justify-between transition-colors"
                  data-testid="show-litigation-calcs"
                >
                  <div className="flex items-center gap-2">
                    <Code className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                    <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Show Calculation Details</span>
                    <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>No AI - Pure Math</span>
                  </div>
                  {showLitigationCalcs ? <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} /> : <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />}
                </button>
                
                {showLitigationCalcs && (
                  <div className="p-6 border-t" style={{ borderColor: 'var(--color-border-sub)', backgroundColor: 'var(--color-bg-elevated)' }}>
                    <div className="space-y-6 font-mono text-sm">
                      {/* NPV Formula */}
                      <div>
                        <h5 className="font-semibold mb-2 font-sans" style={{ color: 'var(--color-text-primary)' }}>1. Net Present Value (NPV) Formula</h5>
                        <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                          <p className="mb-2" style={{ color: 'var(--color-text-secondary)' }}>PV = FV / (1 + r)^n</p>
                          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>where r = 8% annual discount rate, n = years</p>
                          <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
                            <p style={{ color: 'var(--color-text-secondary)' }}>
                              <span style={{ color: 'var(--color-text-muted)' }}>Litigation NPV =</span> {formatCurrency(litigationResult.litigation_path.expected_value_npv)}
                            </p>
                            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                              Future value discounted over {litigationResult.litigation_path.time_to_resolution_months} months ({(litigationResult.litigation_path.time_to_resolution_months / 12).toFixed(2)} years)
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Expected Value Formula */}
                      <div>
                        <h5 className="font-semibold mb-2 font-sans" style={{ color: 'var(--color-text-primary)' }}>2. Expected Value Calculation</h5>
                        <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                          <p className="mb-2" style={{ color: 'var(--color-text-secondary)' }}>EV = Σ(outcome × probability)</p>
                          <div className="mt-3 pt-3 border-t space-y-2" style={{ borderColor: 'var(--color-border-sub)' }}>
                            <p style={{ color: 'var(--color-text-secondary)' }}>
                              <span style={{ color: 'var(--color-verified)' }}>Win (no appeal):</span> {(litigationResult.litigation_path.win_probability * 0.7 * 100).toFixed(1)}% × net_value
                            </p>
                            <p style={{ color: 'var(--color-text-secondary)' }}>
                              <span style={{ color: 'var(--color-needs-human-text)' }}>Win (appeal upheld):</span> {(litigationResult.litigation_path.win_probability * 0.3 * 0.65 * 100).toFixed(1)}% × net_value
                            </p>
                            <p style={{ color: 'var(--color-text-secondary)' }}>
                              <span style={{ color: 'var(--color-needs-human-text)' }}>Win (appeal reversed):</span> {(litigationResult.litigation_path.win_probability * 0.3 * 0.35 * 100).toFixed(1)}% × (-costs)
                            </p>
                            <p style={{ color: 'var(--color-text-secondary)' }}>
                              <span style={{ color: 'var(--color-text-muted)' }}>Lose at trial:</span> {((1 - litigationResult.litigation_path.win_probability) * 100).toFixed(1)}% × (-costs)
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Win Probability */}
                      <div>
                        <h5 className="font-semibold mb-2 font-sans" style={{ color: 'var(--color-text-primary)' }}>3. Win Probability Estimation</h5>
                        <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                          <p className="mb-2" style={{ color: 'var(--color-text-secondary)' }}>Base rate: 55% (plaintiff win rate at trial)</p>
                          <p style={{ color: 'var(--color-text-secondary)' }}>Adjusted by: documentation strength, policy clarity, causation</p>
                          <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
                            <p style={{ color: 'var(--color-text-secondary)' }}>
                              <span style={{ color: 'var(--color-text-muted)' }}>Final Win Probability =</span> <span className="font-bold">{(litigationResult.litigation_path.win_probability * 100).toFixed(1)}%</span>
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Break-Even */}
                      <div>
                        <h5 className="font-semibold mb-2 font-sans" style={{ color: 'var(--color-text-primary)' }}>4. Break-Even Settlement</h5>
                        <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                          <p className="mb-2" style={{ color: 'var(--color-text-secondary)' }}>Break-Even = Expected Litigation NPV</p>
                          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Any settlement above this is better than litigating</p>
                          <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
                            <p style={{ color: 'var(--color-text-secondary)' }}>
                              <span style={{ color: 'var(--color-text-muted)' }}>Break-Even =</span> <span className="font-bold">{formatCurrency(litigationResult.comparison.break_even_settlement)}</span>
                            </p>
                            <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                              <span style={{ color: 'var(--color-text-muted)' }}>Current Offer =</span> {formatCurrency(litigationResult.settlement_path.offer_amount)}
                            </p>
                            <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                              <span style={{ color: 'var(--color-text-muted)' }}>Difference =</span> 
                              <span style={{ color: litigationResult.comparison.settlement_advantage > 0 ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}>
                                {' '}{formatCurrency(litigationResult.comparison.settlement_advantage)} ({litigationResult.comparison.advantage_percentage.toFixed(0)}%)
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Disclaimer */}
                      <div className="p-3 rounded-lg border" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                        <p className="text-xs font-sans" style={{ color: 'var(--color-needs-human-text)' }}>
                          <strong>Note:</strong> These calculations use deterministic mathematical formulas with no AI/LLM involvement. 
                          Same inputs will always produce the same outputs. Default assumptions: 8% discount rate, 30% appeal risk, 
                          33-40% contingency fees. Adjust inputs for case-specific analysis.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
